import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  DispatchIssueWithCache,
  DispatchIssueWithCacheSearch,
  DownloadDispatchIssueWithCacheAsCsvResponse,
  QueryDownloadDispatchIssueWithCacheAsCsvArgs,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { TypedDocumentNode } from '@apollo/client';
import { getProperty, queryInput } from '@/utils';
import graphApi from '@/api';
import { DownloadDispatchIssueWithCacheAsCsvDocument } from '@/api/queries';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  stations?: string[];
  tripServicerIataAirlineCodes?: string[];
} & { [key in keyof DispatchIssueWithCacheSearch]?: any })[];

const dispatchIssueCSVKeys: (keyof DispatchIssueWithCache)[] = [
  'crewMemberGiftCardCount',
  'crewMemberName',
  'crewMemberPhone',
  'datetime',
  'dispatcherFirstName',
  'dispatcherLastName',
  'driverFirstName',
  'driverLastName',
  'flightIssueDescription',
  'flightLate',
  'managerFirstName',
  'managerLastName',
  'outboundFlightInfo',
  'recoveryCompleted',
  'recoveryFlightDatetime',
  'recoveryFlightNumber',
  'recoveryNeeded',
  'rootCause',
  'rootCauseDescription',
  'station',
  'taxiCost',
  'taxiDetails',
  'taxiUsed',
  'tripAirportCode',
  'tripFlightNumber',
  'tripKind',
  'tripServicerIataAirlineCode',
];

const dispatchIssueCSVColumns: Record<keyof DispatchIssueWithCache, true> = Object.fromEntries(
  dispatchIssueCSVKeys.map((key) => [key, true])
) as Record<keyof DispatchIssueWithCache, true>;

interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadDispatchIssueWithCacheAsCsvResponse>;
}

// DispatchIssue CSV Service
export const { downloadDispatchIssueWithCacheCsv, useDownloadDispatchIssueWithCacheCsv } = ((): {
  downloadDispatchIssueWithCacheCsv: ServiceMethod;
  useDownloadDispatchIssueWithCacheCsv: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(DownloadDispatchIssueWithCacheAsCsvDocument).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadDispatchIssueWithCacheAsCsvResponse> => {
        const query: DispatchIssueWithCacheSearch[] = payload?.map((item) => ({
          createdAt: queryInput.date([item?.startDatetime, item.endDatetime]),
          station: queryInput.orNull(item?.stations),
          tripServicerIataAirlineCode: queryInput.orNull(item?.tripServicerIataAirlineCodes),
          taxiUsed: queryInput(item?.taxiUsed),
        }));
        const args: QueryDownloadDispatchIssueWithCacheAsCsvArgs = {
          input: {
            query,
            columns: dispatchIssueCSVColumns,
          },
        };
        const opts = { merge: options?.merge ? 'downloadDispatchIssueWithCacheAsCSV' : undefined };
        const res = await fn(args, opts);
        return getProperty('downloadDispatchIssueWithCacheAsCSV', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    downloadDispatchIssueWithCacheCsv: fetch,
    useDownloadDispatchIssueWithCacheCsv: useService,
  };
})();
